/* assets/src/viewer/contract/facade.js
 * Reimplementazione FASE 6 di window.ASG_Viewer: STESSE firme della facade F5
 * (viewer-contract.js) ma comportamento fluido reale:
 *  - setColorPreview -> cross-fade materiale in-place (niente rebuild)
 *  - focusCategory   -> camera fly-to per categoria
 * I metodi DOM-driven (setTimeOfDay/openHotspot/inspect/playEntrance) sono portati 1:1.
 * Flippa _f5=false per certificare lo swap.
 */
export function installFacade(viewer) {
  const hasMM = !!window.matchMedia;
  const prefersReducedMotion = () => { try { return hasMM && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };
  const raf = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : (cb) => setTimeout(cb, 16);
  const nextFrame = (cb) => raf(() => raf(cb));
  const call = (name, args) => { const fn = window[name]; if (typeof fn !== 'function') return undefined; try { return fn.apply(null, args || []); } catch (e) { return undefined; } };

  const state = {
    category: null, environment: 'terrazza', timeOfDay: 'mezzogiorno',
    inspect: false, previewActive: false, configProvider: null, entrancePlayed: false,
  };
  const TOD_HOURS = { mezzogiorno: 12, golden: 18, tramonto: 20 };

  const currentModel = () => (viewer.controller ? viewer.controller.model : null);

  const ASG_Viewer = {
    _installed: true,
    _f5: false, // FASE 6: swap certificato
    _state: state,

    registerConfigProvider(fn) { if (typeof fn === 'function') state.configProvider = fn; return this; },
    isReady() { return typeof window.ASG_UpdateScene === 'function'; },
    setQuality(level) { if (viewer.setQuality) viewer.setQuality(level); return this; },

    // F6: fly-to morbido sulla zona pertinente alla categoria.
    focusCategory(cat, opts) {
      opts = opts || {};
      state.category = cat || null;
      if (cat === 'finishes') call('ASG_DisableAutoRotate');
      if (!opts.noReframe) viewer.focus(cat);
      return this;
    },

    // F6: preview colore via cross-fade materiale (no rebuild). hex=null => ripristina committato.
    setColorPreview(target, hex) {
      const model = currentModel();
      if (!model) return false;
      if (hex == null) {
        if (!state.previewActive) return false;
        state.previewActive = false;
        const prev = viewer.controller.prevNorm;
        if (prev) {
          if (target === 'telo') { if (model.setFabricColor) model.setFabricColor(prev.fabricColor); }
          else if (model.setStructureColor) model.setStructureColor(prev.structColor);
        }
        viewer.requestRender();
        return true;
      }
      state.previewActive = true;
      if (target === 'telo') { if (model.setFabricColor) model.setFabricColor(hex); }
      else if (model.setStructureColor) model.setStructureColor(hex);
      viewer.requestRender();
      return true;
    },

    commitColor() { state.previewActive = false; return this; },

    setEnvironmentPreset(preset) {
      if (preset) state.environment = preset;
      // F6: il preset system del viewer gestisce preset + visibilità in un solo build
      // (niente più doppio trigger con ASG_ToggleFurniture come in F5).
      if (viewer.setEnvironmentPreset) viewer.setEnvironmentPreset(state.environment);
      else call('ASG_ToggleFurniture', [true]);
      window.ASG_FurnitureActive = true;
      return state.environment;
    },

    // Port 1:1 da F5: pilota lo slider canonico #asg-shadow-hour (così popover/pills concordano).
    setTimeOfDay(preset) {
      if (TOD_HOURS[preset] == null) return false;
      state.timeOfDay = preset;
      const hour = TOD_HOURS[preset];
      const slider = document.getElementById('asg-shadow-hour');
      if (slider) {
        slider.value = String(hour);
        const label = document.getElementById('asg-shadow-hour-label');
        if (label) label.textContent = (hour < 10 ? '0' : '') + hour + ':00';
        try { slider.dispatchEvent(new Event('input', { bubbles: true })); }
        catch (e) { const ev = document.createEvent('HTMLEvents'); ev.initEvent('input', true, false); slider.dispatchEvent(ev); }
      } else {
        let orientation = 'south';
        const os = document.getElementById('asg-shadow-orientation');
        if (os && os.value) orientation = os.value;
        call('ASG_UpdateSunSettings', [{ enabled: true, orientation, hour }]);
      }
      return true;
    },

    // Port 1:1 da F5: delega all'affordance DOM corrispondente (gli hotspot 3D reali
    // sono gestiti dal modulo interaction/hotspots che simula questi click).
    openHotspot(target) {
      const map = { roof: 'btn-toggle-roof', furniture: 'btn-toggle-furniture', shadow: 'btn-open-config-panel', reset: 'btn-reset-view' };
      const id = map[target];
      if (id) { const el = document.getElementById(id); if (el) { el.click(); return true; } }
      return false;
    },
    closeHotspot() { return this; },

    enterInspect() {
      const stack = document.querySelector('.asg-viewer-stack') || document.querySelector('.asg-viewer-col');
      if (!stack) return false;
      state.inspect = true;
      const wrap = document.getElementById('asg-configuratore-wrapper');
      if (wrap) wrap.setAttribute('data-asg-inspect', '1');
      stack.classList.add('asg-viewer-inspect');
      call('ASG_DisableAutoRotate');
      if (stack.requestFullscreen) { try { stack.requestFullscreen(); } catch (e) { /* no-op */ } }
      nextFrame(() => { call('ASG_OnWindowResize'); call('ASG_RequestRender'); });
      return true;
    },
    exitInspect() {
      const stack = document.querySelector('.asg-viewer-stack') || document.querySelector('.asg-viewer-col');
      state.inspect = false;
      const wrap = document.getElementById('asg-configuratore-wrapper');
      if (wrap) wrap.removeAttribute('data-asg-inspect');
      if (stack) stack.classList.remove('asg-viewer-inspect');
      if (document.fullscreenElement && document.exitFullscreen) { try { document.exitFullscreen(); } catch (e) { /* no-op */ } }
      nextFrame(() => { call('ASG_OnWindowResize'); call('ASG_RequestRender'); });
      return true;
    },
    toggleInspect() { return state.inspect ? this.exitInspect() : this.enterInspect(); },
    isInspecting() { return !!state.inspect; },

    playEntrance(opts) {
      opts = opts || {};
      if (state.entrancePlayed) return this;
      state.entrancePlayed = true;
      const col = document.querySelector('.asg-viewer-col');
      if (!col) return this;
      if (prefersReducedMotion()) { col.classList.remove('asg-viewer-booting'); col.classList.add('asg-viewer-booted'); return this; }
      col.classList.add('asg-viewer-booting');
      const delay = typeof opts.delay === 'number' ? opts.delay : 520;
      setTimeout(() => { col.classList.remove('asg-viewer-booting'); col.classList.add('asg-viewer-booted'); call('ASG_RequestRender'); }, delay);
      return this;
    },
  };

  try {
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement && state.inspect) ASG_Viewer.exitInspect();
    });
  } catch (e) { /* no-op */ }

  window.ASG_Viewer = ASG_Viewer;
  return ASG_Viewer;
}
